'use client';
import { encode, ParsedUrlQuery } from 'querystring';

import { ReactNode, useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useFindAirportByAirportCode } from '@components/views/flight/hooks/flight.query';
import {
  convertDateToMomentObject,
  DateType,
  FLIGHT_SEARCH_DATE_FORMAT,
  formatMomentDate,
} from '@utils/dateUtil';
import {
  FlightGuestProps,
  FlightTripType,
  MobileSearchProps,
} from '@components/views/flight/types';
import useSearchBar from '@components/views/flight/hooks/useSearchBar';
import {
  FLIGHT_SEARCH_QUERY_PARAM_KEYS,
  INBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS,
  OUTBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS,
} from '@components/views/flight/constants';
import { InitialFormValues } from '@components/views/types';
import { useToggle } from '@hooks/common';
import { paramsToObject } from '@components/views/utils';

import { generateFlightSearchQuery, isDomesticFlight } from '../../../utils';

const FlightSearchBar = dynamic(() => import('./FlightSearchBar'), {
  ssr: false,
});

const FlightSearchBarContainer = ({
  showIsCheckbox,
  handleResearch,
  isStickySearch,
  isSearchResultHeaderVisible,
  defaultSearchModalVisible,
  onCancel,
  title,
  searchButtonTitle,
  startDateResponsive,
  endDateResponsive,
  isHoliday,
}: {
  showIsCheckbox?: boolean;
  handleResearch?: () => void;
  isStickySearch?: boolean;
  initialValues?: InitialFormValues;
  isSearchResultHeaderVisible?: boolean;
  defaultSearchModalVisible?: boolean;
  title?: ReactNode;
  searchButtonTitle?: ReactNode;
  onCancel?: () => void;
  startDateResponsive?: DateType;
  endDateResponsive?: DateType;
  isHoliday?: boolean;
}) => {
  const {
    flightTripType,
    onChangeFlightTripType,
    startDate,
    endDate,
    onDatesChange,
    onDateChange,
    departureAirportSearchText,
    departureAirport,
    onDepartureAirportSelect,
    onDepartureAirportTextChange,
    arrivalAirportSearchText,
    arrivalAirport,
    onArrivalAirportTextChange,
    onArrivalAirportSelect,
    guestInfo,
    onApplyGuestsForm,
    handleSubmit,
    errors,
  } = useSearchBar();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const query: ParsedUrlQuery = paramsToObject(searchParams?.entries());

  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const {
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureAirportCode]: departureAirportCode,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalAirportCode]: arrivalAirportCode,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]: departureDate,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]: returnDate,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.adults]: adults = '1',
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.childs]: childs,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.infants]: infants,
    [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay]: isOneWay = 'false',
  } = query;

  const { data: departureAirportData } = useFindAirportByAirportCode(
    departureAirportCode as string
  );
  const { data: arrivalAirportData } = useFindAirportByAirportCode(
    arrivalAirportCode as string
  );

  useEffect(() => {
    if (departureAirportData) {
      const foundAirport = departureAirportData?.[0];
      onDepartureAirportSelect(foundAirport);
    }
  }, [departureAirportData]);

  useEffect(() => {
    if (arrivalAirportData) {
      const foundAirport = arrivalAirportData?.[0];
      onArrivalAirportSelect(foundAirport);
    }
  }, [arrivalAirportData]);

  useEffect(() => {
    if (isOneWay) {
      onChangeFlightTripType(isOneWay === 'true' ? 'one-way' : 'round-trip');
    }
  }, [isOneWay]);

  useEffect(() => {
    if (flightTripType === 'one-way') {
      onDateChange(
        departureDate
          ? convertDateToMomentObject(
              departureDate as string,
              FLIGHT_SEARCH_DATE_FORMAT
            )
          : null
      );
    } else if (flightTripType === 'round-trip') {
      onDatesChange({
        startDate: departureDate
          ? convertDateToMomentObject(
              departureDate as string,
              FLIGHT_SEARCH_DATE_FORMAT
            )
          : null,
        endDate: returnDate
          ? convertDateToMomentObject(
              returnDate as string,
              FLIGHT_SEARCH_DATE_FORMAT
            )
          : null,
      });
    }
  }, [departureDate, returnDate, flightTripType]);

  useEffect(() => {
    const adultsCount = adults ? parseInt(adults as string) : 0;
    const guests: FlightGuestProps = {
      numberOfAdultGuests: adultsCount,
      numberOfChildGuests: childs ? parseInt(childs as string) : 0,
      numberOfInfantGuests: infants ? parseInt(infants as string) : 0,
    };
    onApplyGuestsForm(guests);
  }, [adults, childs, infants]);

  const pushDataToUrl = ({
    flightTripType,
    departureAirportCode,
    departureIsCity,
    arrivalAirportCode,
    arrivalIsCity,
    departureDate,
    returnDate,
    numberOfAdultGuests,
    numberOfChildGuests,
    numberOfInfantGuests,
    isDomestic,
    isDirectFlight,
  }: {
    flightTripType?: FlightTripType;
    departureAirportCode?: string;
    departureIsCity?: '1' | undefined;
    arrivalAirportCode?: string;
    arrivalIsCity?: '1' | undefined;
    isDirectFlight?: 'true' | 'false';
    departureDate: DateType | null;
    returnDate: DateType | null;
    numberOfAdultGuests?: number;
    numberOfChildGuests?: number;
    numberOfInfantGuests?: number;
    isDomestic?: 'true' | 'false';
  }) => {
    const nextQuery = {
      ...query,
    };

    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureAirportCode,
      departureAirportCode
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureIsCity,
      departureIsCity
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalAirportCode,
      arrivalAirportCode
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalIsCity,
      arrivalIsCity
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate,
      formatMomentDate(departureDate, FLIGHT_SEARCH_DATE_FORMAT)
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate,
      flightTripType === 'round-trip'
        ? formatMomentDate(returnDate, FLIGHT_SEARCH_DATE_FORMAT)
        : undefined
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.adults,
      numberOfAdultGuests ? numberOfAdultGuests.toString() : undefined
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.childs,
      numberOfChildGuests ? numberOfChildGuests?.toString() : undefined
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.infants,
      numberOfInfantGuests ? numberOfInfantGuests.toString() : undefined
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay,
      flightTripType === 'one-way' ? 'true' : 'false'
    );
    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDirectFlight,
      isDirectFlight
    );
    generateFlightSearchQuery(
      nextQuery,
      INBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS.STOPS,
      isDirectFlight === 'true' ? '1' : undefined
    );
    generateFlightSearchQuery(
      nextQuery,
      OUTBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS.STOPS,
      isDirectFlight === 'true' ? '1' : undefined
    );

    generateFlightSearchQuery(
      nextQuery,
      FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic,
      isDomestic
    );
    const nextPathname =
      isDomestic === 'true' ? '/ic-hat-ucuslari' : '/dis-hat-ucuslari';
    if (
      pathname?.includes('ic-hat-ucuslari') ||
      pathname?.includes('dis-hat-ucuslari')
    ) {
      //clear all filter before search
      const queryParams = [
        {
          ...INBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS,
        },
        { ...OUTBOUND_FLIGHT_FILTER_QUERY_PARAM_KEYS },
      ];

      queryParams.forEach((element) => {
        Object.entries(element).forEach((i) => delete nextQuery[i[1]]);
      });

      const nextQueryParams = new URLSearchParams(
        nextQuery as Record<string, string>
      )?.toString();
      router.push(`/${nextPathname}?${nextQueryParams}`);
    } else {
      //If page is not flight listing page, we are using window.open because when we use push isDirectFlight filter is not working as expected.
      window.open(`${nextPathname}?${encode(nextQuery)}`, '_self');
    }
  };
  const [isDirectFlights, onChangeIsDirectFlight] = useToggle();
  const onResearch = () => {
    setButtonDisable?.(false);
    handleResearch && handleResearch();
    pushDataToUrl({
      flightTripType,
      isDirectFlight: isDirectFlights ? 'true' : 'false',
      departureAirportCode: departureAirport?.airportCode,
      departureIsCity: departureAirport?.isCity ? '1' : undefined,
      arrivalAirportCode: arrivalAirport?.airportCode,
      arrivalIsCity: arrivalAirport?.isCity ? '1' : undefined,
      departureDate: startDate,
      returnDate: endDate,
      numberOfAdultGuests: guestInfo?.numberOfAdultGuests,
      numberOfChildGuests: guestInfo?.numberOfChildGuests,
      numberOfInfantGuests: guestInfo?.numberOfInfantGuests,
      isDomestic: isDomesticFlight(departureAirport, arrivalAirport)
        ? 'true'
        : 'false',
    });
  };
  const onMobileSearch = ({
    flightTripType,
    startDate,
    endDate,
    departureAirport,
    arrivalAirport,
    guestInfo,
  }: MobileSearchProps) => {
    handleResearch && handleResearch();
    pushDataToUrl({
      flightTripType,
      departureAirportCode: departureAirport?.airportCode,
      departureIsCity: departureAirport?.isCity ? '1' : undefined,
      arrivalAirportCode: arrivalAirport?.airportCode,
      arrivalIsCity: arrivalAirport?.isCity ? '1' : undefined,
      departureDate: startDate,
      returnDate: endDate,
      numberOfAdultGuests: guestInfo?.numberOfAdultGuests,
      numberOfChildGuests: guestInfo?.numberOfChildGuests,
      numberOfInfantGuests: guestInfo?.numberOfInfantGuests,
      isDomestic: isDomesticFlight(departureAirport, arrivalAirport)
        ? 'true'
        : 'false',
      isDirectFlight: isDirectFlights ? 'true' : 'false',
    });
  };

  return (
    <FlightSearchBar
      setButtonDisable={setButtonDisable}
      disable={buttonDisable}
      showIsCheckbox={showIsCheckbox}
      isDirectFlight={isDirectFlights}
      onChangeIsDirectFlight={onChangeIsDirectFlight}
      flightTripType={flightTripType}
      onChangeFlightTripType={onChangeFlightTripType}
      startDate={startDateResponsive || startDate}
      endDate={endDateResponsive || endDate}
      onDatesChange={onDatesChange}
      onDateChange={onDateChange}
      departureAirportSearchText={departureAirportSearchText}
      departureAirport={departureAirport}
      onDepartureAirportTextChange={onDepartureAirportTextChange}
      onDepartureAirportSelect={onDepartureAirportSelect}
      arrivalAirportSearchText={arrivalAirportSearchText}
      arrivalAirport={arrivalAirport}
      onArrivalAirportTextChange={onArrivalAirportTextChange}
      onArrivalAirportSelect={onArrivalAirportSelect}
      guestInfo={guestInfo}
      onApplyGuestsForm={onApplyGuestsForm}
      onResearch={onResearch}
      onMobileResearch={onMobileSearch}
      handleSubmit={handleSubmit}
      errors={errors}
      isStickySearch={isStickySearch}
      isSearchResultHeaderVisible={isSearchResultHeaderVisible}
      defaultSearchModalVisible={defaultSearchModalVisible}
      onCancel={onCancel}
      title={title}
      searchButtonTitle={searchButtonTitle}
      isHoliday={isHoliday}
    />
  );
};

export default FlightSearchBarContainer;
